import { createRoot } from "react-dom/client";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";

import { BrowserRouter, Route, useLocation } from "react-router-dom";
import { API_UserNotifications } from "../../src/apidefs";
import { api } from "./api";
import { CompoPage } from "./Compo/CompoPage";
import { AllComposPage } from "./Compo/AllComposPage";
import SubmitEntryPage from "./Compo/Submit/SubmitEntryPage";
import { CompoAdminView } from "./Admin/CompoAdminPage";
import { CompoBadVoters } from "./Admin/CompoBadVoters";
import { AdminToolbar } from "./Admin/AdminToolbar";
import { EntryFavoritesPage } from "./Entry/EntryFavoritesPage";
import { EntryPage } from "./Entry/EntryPage";
import { EditEntryPage } from "./Entry/EditEntryPage";
import { FeedPage } from "./Feed/FeedPage";
import { NavToolbar } from "./Toolbar/NavToolbar";
import { Toolbar } from "./Toolbar/Toolbar";
import { ForgotPasswordPage } from "./Auth/ForgotPasswordPage";
import { LoginPage } from "./Auth/LoginPage";
import { RegisterPage } from "./Auth/RegisterPage";
import { ResetPasswordPage } from "./Auth/ResetPasswordPage";
import { UserView } from "./User/UserPage";
import { AllCommentsPage } from "./User/AllCommentsPage";
import { NotificationsPage } from "./Notifications/NotificationsPage";
import { EditProfile } from "./User/EditProfile";
import { JudgePage } from "./Compo/JudgePage";
import { ResultsPage } from "./Compo/Results/ResultsPage";
import { IndividualResultsPage } from "./Compo/Results/IndividualResultsPage";
import { EntryStatsPage } from "./Entry/Stats/EntryStatsPage";
import { RegistryPage } from "./User/RegistryPage";
import { MostIntelligentPage } from "./User/MostIntelligentPage";
import { NewCompoPage } from "./Compo/NewCompoPage";
import { NewEventPage } from "./Event/NewEventPage";
import { AllPlaysPage } from "./User/AllPlaysPage";
import AllFavoritesView from "./User/AllFavoritesPage";
import { ExplainGoldPage } from "./Explain/ExplainGoldPage";
import { CompoRequiredVotesPage } from "./Admin/CompoRequiredVotesPage";
import { EditEventView } from "./Event/EditEvent";
import { ExplainChipsPage } from "./Explain/ExplainChipsPage";
import { ThemeSuggestPage } from "./Compo/ThemeSuggestPage";
import { ModFeedPage } from "./Admin/ModFeedPage";
import { AllReviewsPage } from "./User/AllReviewsPage";
import { GuessTablePage } from "./Compo/GuessTablePage";
import { TagExplorerPage } from "./Tag/TagExplorerPage";
import { TopEntriesPage } from "./Entry/TopEntriesPage";
import { ExplainSupportersPage } from "./Explain/ExplainSupportersPage";
import { ForumPage } from "./Forum/ForumPage";
import { NewTopicPage } from "./Forum/NewTopicPage";
import { ForumTopicPage } from "./Forum/ForumTopicPage";
import { FutureComposPage } from "./Compo/FutureComposPage";
import { RandomEntryPage } from "./Entry/RandomEntryPage";
import { UserProvider } from "./state";
import { Footer } from "./Layout/Footer";
import { ErrorDisplay } from "./Layout/ErrorDisplay";
import { ToastDisplay } from "./Layout/ToastDisplay";
import { ScrollToTop } from "./Layout/ScrollToTop";
import { PromptModalProvider } from "./DesignSystem/PromptModal";
import { CompoVoteAuditPage } from "./Explain/CompoVoteAuditPage";
import { ExplainCacaophonyPage } from "./Explain/ExplainCacaophonyPage";
import { ExplainXPPage } from "./Explain/ExplainXPPage";
import { ChipsCacaophonyPage } from "./Explain/ChipsCacaophonyPage";
import { HomePage } from "./Home/HomePage";
import { SupportPage } from "./Admin/SupportPage";
import { ImpersonationBanner } from "./Layout/ImpersonationBanner";
import { SyncListenEditPage } from "./SyncListen/SyncListenEditPage";
import { SyncListenPage } from "./SyncListen/SyncListenPage";

const AppRoutes = (): JSX.Element => {
  const [notifications, setNotifications] =
    useState<API_UserNotifications | null>(null);
  const location = useLocation();

  const setNotificationsCount = useCallback(
    (count: number) => {
      if (notifications) {
        setNotifications({ ...notifications, unread_count: count });
      }
    },
    [notifications]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchNotifications = async () => {
      const nextNotifications = await api.get("/api/notifications");
      if (isMounted) {
        setNotifications(nextNotifications);
      }
    };

    fetchNotifications();

    return () => {
      isMounted = false;
    };
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen flex-col bg-slate-100 dark:bg-slate-900">
      <ImpersonationBanner />
      <div className={`flex-1 ${location.pathname.match(/^\/compo\/\d+\/sync$/) ? "" : "pt-16"}`}>
        <Route path="/compo/:id/admin" component={AdminToolbar} />
        <Route path="*" component={ErrorDisplay} />
        <Route path="*" component={ToastDisplay} />

        <Route exact strict path="/" component={HomePage} />

        <Route exact path={`/compo/:id`} component={CompoPage} />
        <Route exact path={`/feed`} component={FeedPage} />
        <Route exact path={`/tag/:tag_slug`} component={TagExplorerPage} />
        <Route exact path={`/mod-feed`} component={ModFeedPage} />
        <Route exact path={`/admin/compo/new`} component={NewCompoPage} />
        <Route exact path={`/admin/event/new`} component={NewEventPage} />
        <Route exact path={`/admin/event/:id`} component={EditEventView} />
        <Route exact path={`/admin/support`} component={SupportPage} />
        <Route exact path={`/compos`} component={AllComposPage} />
        <Route exact path={`/compos/future`} component={FutureComposPage} />
        <Route exact path={`/compo/:id/theme`} component={ThemeSuggestPage} />
        <Route
          exact
          path={`/compo/:id/guesstable`}
          component={GuessTablePage}
        />
        <Route exact path={`/compo/:id/submit`} component={SubmitEntryPage} />
        <Route exact path={`/compo/:id/judge`} component={JudgePage} />
        <Route exact path={`/compo/:id/admin`} component={CompoAdminView} />
        <Route
          exact
          path={`/compo/:id/admin/badvoters`}
          component={CompoBadVoters}
        />
        <Route
          exact
          path={`/compo/:id/admin/voteaudit`}
          component={CompoVoteAuditPage}
        />
        <Route
          exact
          path={`/compo/:id/admin/votesrequired`}
          component={CompoRequiredVotesPage}
        />
        <Route
          exact
          path={`/compo/:id/sync/edit`}
          component={SyncListenEditPage}
        />
        <Route exact path={`/compo/:id/sync`} component={SyncListenPage} />
        <Route exact path={`/compo/:id/results`} component={ResultsPage} />
        <Route
          exact
          path={`/compo/:id/results/:type/:category_id`}
          component={IndividualResultsPage}
        />

        <Route exact path={`/cacaophony`} component={ChipsCacaophonyPage} />
        <Route
          exact
          path={`/cacaophony/explain`}
          component={ExplainCacaophonyPage}
        />

        <Route exact path={`/forum`} component={ForumPage} />
        <Route exact path={`/forum/new-topic`} component={NewTopicPage} />
        <Route exact path={`/forum/topic/:id`} component={ForumTopicPage} />

        <Route exact path={`/edit/profile`} component={EditProfile} />
        <Route exact path={`/registry`} component={RegistryPage} />
        <Route
          exact
          path={`/most-intelligent`}
          component={MostIntelligentPage}
        />
        <Route exact path={`/entries`} component={TopEntriesPage} />
        <Route exact path={`/entries/:user`} component={TopEntriesPage} />
        <Route exact path={`/random`} component={RandomEntryPage} />
        <Route exact path={`/entry/:id`} component={EntryPage} />
        <Route
          exact
          path={`/entry/:id/favorites`}
          component={EntryFavoritesPage}
        />
        <Route exact path={`/entry/:id/stats`} component={EntryStatsPage} />
        <Route exact path={`/entry/:id/edit`} component={EditEntryPage} />
        <Route exact path={`/user/:username`} component={UserView} />
        <Route
          exact
          path={`/notifications`}
          render={() => (
            <NotificationsPage
              notifications={notifications}
              setNotificationsCount={setNotificationsCount}
            />
          )}
        />

        <Route
          exact
          path={`/user/:username/comments`}
          component={AllCommentsPage}
        />
        <Route
          exact
          path={`/user/:username/reviews`}
          component={AllReviewsPage}
        />
        <Route exact path={`/user/:username/plays`} component={AllPlaysPage} />
        <Route
          exact
          path={`/user/:username/favorites`}
          component={AllFavoritesView}
        />
        <Route exact path={`/login`} component={LoginPage} />
        <Route exact path={`/forgot-password`} component={ForgotPasswordPage} />
        <Route exact path={`/reset-password`} component={ResetPasswordPage} />
        <Route exact path={`/register`} component={RegisterPage} />
        <Route exact path={`/explain-xp`} component={ExplainXPPage} />
        <Route
          exact
          path={`/explain-supporters`}
          component={ExplainSupportersPage}
        />
        <Route
          exact
          path={`/explain-everything`}
          component={ExplainChipsPage}
        />
        <Route exact path={`/explain-gold`} component={ExplainGoldPage} />
      </div>

      {!location.pathname.match(/^\/compo\/\d+\/sync$/) && (
        <>
          <div className="bg-[#212522] text-white">
            <Footer />
            <div
              aria-hidden="true"
              style={{ height: "var(--playback-bar-height, 92px)" }}
            />
          </div>
          <Route
            path="*"
            render={(props) => (
              <NavToolbar {...props} notifications={notifications} />
            )}
          />
          <Route path="*" render={() => <Toolbar />} />
        </>
      )}
      <PromptModalProvider />
    </div>
  );
};

const rootElement = document.getElementById("main");

if (!rootElement) {
  throw new Error("Main root element not found");
}

createRoot(rootElement).render(
  <UserProvider>
    <BrowserRouter>
      <ScrollToTop>
        <AppRoutes />
      </ScrollToTop>
    </BrowserRouter>
  </UserProvider>
);
